import {
    Component,
    h,
    Host,
    Prop,
} from '@stencil/core';
import { ThemeType } from '@uipath/portal-shell-util';

import { AccountType } from '../../constants/constants';
import { EcommerceEvent } from '../../models/appInsights/events/ecommerce.events';
import store from '../../store/store';
import { accountAndTenantsStore } from '../../utils/accountAndTenants/accountAndTenants';
import { Features } from '../../utils/featureFlags/featureFlagConstants';
import { getFeatureFlag } from '../../utils/featureFlags/featureFlags';
import {
    caseInsensitiveCompare,
    translatedContactSalesUrl,
} from '../../utils/helper';
import { getLanguage } from '../../utils/languageHelper';
import { t } from '../../utils/localization/loc';
import Logger from '../../utils/logger/logger';
import { getOrganizationStandardUrl } from '../../utils/orgStandardUrlHelper';
import { getTheme } from '../../utils/themeHelper';

@Component({
    tag: 'portal-enterprise-trial-internal',
    styleUrl: 'portal-enterprise-trial-internal.scss',
    shadow: true,
})
export class PortalEnterpriseTrialInternal {
    /**
    * Theme override to bypass store state
    * @memberof PortalEnterpriseTrialInternal
    */
    @Prop() theme: ThemeType = 'autoTheme';

    /**
    * Provide end date of the trial
    * @memberof PortalEnterpriseTrialInternal
    */
    @Prop() endDate: number = 0;

    /**
     * Provide subscription type of the trial
     * @memberof PortalEnterpriseTrialInternal
     */
    @Prop() subscriptionType: AccountType;

    render() {
        const enableEcommercePlanSelectionRevampM0 = getFeatureFlag(Features.EnableEcommercePlanSelectionRevampM0.name);
        const landingPage = enableEcommercePlanSelectionRevampM0 ? '/portal_/buyPresets' : '/portal_/buypro';
        return (
            <Host class={getTheme(store, this.theme)}>
                <div class='time-left' data-test='time-left'>
                    {this.getTimeLeft(new Date().getTime(), new Date(this.endDate * 1000).getTime())}
                </div>
                {this.shouldShowBuyNowButton() ? (
                    <portal-button
                        data-test='buy-now-button'
                        label={t('buy-now', getLanguage(store))}
                        type='primary'
                        size='sm'
                        href={getOrganizationStandardUrl({ shouldIncludeAccountNameOnK8s: true }) + landingPage}
                        onClick={this.onBuyNowClicked}
                        theme={this.theme}
                    />
                ) : (
                    <portal-button
                        class="contact-sales-button"
                        label={t('contact-sales', getLanguage(store))}
                        type='primary'
                        size='sm'
                        href={translatedContactSalesUrl(getLanguage(store))}
                        onClick={this.onContactSalesClicked}
                        theme={this.theme}
                    />
                )}
            </Host>
        );
    }

    private shouldShowBuyNowButton = () => {
        const accountCountry = accountAndTenantsStore.get('organization')?.country;
        const enableEcommerceInJapan = getFeatureFlag(Features.EnableEcommerceInJapan.name);

        return caseInsensitiveCompare(accountCountry ?? '', 'JP') ? enableEcommerceInJapan : true;
    };

    private onBuyNowClicked = () => {
        Logger.logCustomEvent(EcommerceEvent.UpgradeToPro, {
            invocationMethod: 'HeaderTrialComponentUpgradeToPro',
            subscriptionType: this.subscriptionType,
        });
    };

    private onContactSalesClicked = () => {
        Logger.logCustomEvent(EcommerceEvent.ClickContactSales, {
            invocationMethod: 'HeaderTrialComponentContactSales',
            subscriptionType: this.subscriptionType,
        });
    };

    private getTimeLeft(currentDate: number, endDate: number) {
        const hour = 60 * 60 * 1000;

        const differenceInHours = Math.round(
            (endDate - currentDate) / hour);
        if (differenceInHours > 24) {
            const differenceInDays = Math.min(Math.max(0, Math.round(differenceInHours / 24)), 60);
            return t('client-days', getLanguage(store), { differenceInDays: `${ differenceInDays }` });
        } else if (differenceInHours > 0) {
            return t('client-hours', getLanguage(store), { differenceInHours: `${ differenceInHours }` });
        }
        return '';
    }
}
